import { observer } from 'mobx-react';
import React, { useCallback, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Icons } from 'src/assets/fonts/Icons/icons';
import { SCREENS } from 'src/constants/screens';
import { USER_ROLES } from 'src/constants/user';
import { localeStore } from 'src/mobx/localesStore';
import { userStore } from 'src/mobx/userStore';
import { history } from 'src/services/history';
import { LoadingStatus, TBooleanInNumber } from 'src/types/main';
import { IUserV4 } from 'src/types/user';
import { confirmDelete } from 'src/utils/modalConfirmations';
import { TOOLTIP_DURATION, TOOLTIP_POSITION } from 'src/view/components/Controls/CustomTooltip/CustomTooltip';
import Icon from 'src/view/components/Controls/Icon/Icon';
import Switcher from 'src/view/components/Controls/Switcher/Switcer';
import Text, { TEXT } from 'src/view/components/Text/Text';
import usersStore from 'src/view/screens/SettingsScreen/store/usersStore';
import { appStore } from '~/mobx/appStore';
import TooltipIcon from '~/view/components/TooltipIcon/TooltipIcon';
import './style.scss';

interface IProps {
  user?: IUserV4;
  index?: number;
}

const UserListRow: React.FC<IProps> = observer((props: IProps): JSX.Element => {
  const disabled: boolean = [LoadingStatus.Init, LoadingStatus.Refreshing, LoadingStatus.Loading].includes(usersStore.usersLoadingStatus);

  const editUser = (event: React.MouseEvent<HTMLDivElement>) => {
    if (
      !props.user ||
      (event.target as HTMLDivElement).classList.contains('icon-warning') ||
      (event.target as HTMLDivElement).classList.contains('tooltip__overlay')
    ) {
      return;
    }

    history.push(SCREENS.SETTINGS_USERS_EDIT(props.user.id));
  };

  const deleteUser = () => {
    confirmDelete({
      title: localeStore.t('txt_user_will_be_deleted'),
      message: localeStore.t('delete_user_header'),
      onConfirm: () => props.user && usersStore.deleteUser(props.user.id),
    });
  };

  const isFranchiseEnabled = useMemo((): boolean => appStore.isAddonsAvailable.franchise, [appStore.isAddonsAvailable.franchise]);

  const changeStatus = useCallback(
    (status: TBooleanInNumber) => {
      if (!props.user) return;
      usersStore.updateStatus(props.user.id, status);
    },
    [props.user]
  );

  const renderSwitcher = useCallback(
    (status: TBooleanInNumber, onChange: (status: TBooleanInNumber) => void): JSX.Element | null => {
      if (!props.user) return <Skeleton width={50} height={30} style={{ borderRadius: 30 }} />;

      if (props.user.is_delete) return null;

      return <Switcher status={status} disabled={disabled} onChange={onChange} />;
    },
    [disabled, props.user]
  );

  const franchiseeName = (_user: IUserV4) => {
    const userFranchises = _user.franchises ?? [];
    if (userFranchises.length <= 0) {
      return '';
    }
    return userFranchises[0].title;
  };

  const branchesCountText = (_user: IUserV4) => {
    const userBranches = _user.branches?.length;
    if (usersStore.totalBranches === userBranches || _user.roles.map((role) => role.name).includes(USER_ROLES.RESTAURANT_MANAGER)) {
      return localeStore.t('all_branches');
    }
    let count = 0;
    if (userBranches !== undefined) {
      count = userBranches;
    }
    return `${count} ${localeStore.t('branches').toLowerCase()}`;
  };

  const userRole = (_user?: IUserV4) => {
    if (!_user) {
      return '';
    }
    if (!_user.roles || _user.roles.length <= 0 || !_user.roles[0].name) {
      return '';
    }
    return `${_user.roles[0].name}_role`;
  };

  const { user } = props;
  const isMyAccount = user?.id === userStore.user.id;
  return (
    <li className={`items-list-row ${isMyAccount ? 'items-list-row__my_account' : ''}`}>
      {/* name */}
      <div
        onClick={editUser}
        className={`items-list-row__border d-flex flex-row align-items-center cursor-pointer ${!isFranchiseEnabled ? 'w-15' : 'w-13'}`}
      >
        {user ? (
          <Text type={TEXT.LIST_ROW} text={`${user.first_name || ''} ${user.last_name || ''}`} noTranslate defaultValue="" />
        ) : (
          <Skeleton width={70} height={19.2} />
        )}
      </div>
      {/* branches */}
      <div
        onClick={editUser}
        className={`items-list-row__border d-flex flex-row align-items-center cursor-pointer ${!isFranchiseEnabled ? 'w-14' : 'w-12'}`}
      >
        {user ? <Text type={TEXT.LIST_ROW} text={branchesCountText(user)} noTranslate defaultValue="" /> : <Skeleton width={70} height={19.2} />}
      </div>
      {/* roles */}
      <div
        onClick={editUser}
        className={`items-list-row__border d-flex flex-row align-items-center cursor-pointer ${!isFranchiseEnabled ? 'w-16' : 'w-14'}`}
      >
        {user ? <Text type={TEXT.LIST_ROW} text={userRole(user)} defaultValue="" /> : <Skeleton width={70} height={19.2} />}
      </div>
      {/* phone */}
      <div
        onClick={editUser}
        className={`items-list-row__border d-flex flex-row align-items-center cursor-pointer ${!isFranchiseEnabled ? 'w-15' : 'w-13'}`}
      >
        {user ? <Text dir="ltr" type={TEXT.LIST_ROW} text={user.mobile} defaultValue="" /> : <Skeleton width={50} height={19.2} />}
      </div>
      {/* franchasee */}
      {isFranchiseEnabled && (
        <div onClick={editUser} className="items-list-row__border d-flex flex-row align-items-center cursor-pointer w-12">
          {user ? <Text type={TEXT.LIST_ROW} text={franchiseeName(user)} defaultValue="" /> : <Skeleton width={50} height={19.2} />}
        </div>
      )}
      {/* login */}
      <div
        onClick={editUser}
        className={`items-list-row__border d-flex flex-row align-items-center cursor-pointer ${!isFranchiseEnabled ? 'w-15' : 'w-13'}`}
      >
        {user ? <Text type={TEXT.LIST_ROW} text={user.username} defaultValue="" /> : <Skeleton width={50} height={19.2} />}
      </div>
      {/* email */}
      <div
        onClick={editUser}
        className={`items-list-row__border d-flex flex-row align-items-center cursor-pointer ${!isFranchiseEnabled ? 'w-17' : 'w-15'}`}
      >
        {user ? <Text type={TEXT.LIST_ROW} text={user.email} defaultValue="" /> : <Skeleton width={50} height={19.2} />}
      </div>
      {isMyAccount ? (
        <div
          className={`items-list-row__border d-flex flex-row align-items-center cursor-pointer ${
            !isFranchiseEnabled ? 'w-11' : 'w-8'
          } justify-content-end`}
        >
          <TooltipIcon
            message="tooltip_default_account_delete"
            duration={TOOLTIP_DURATION.EXTRA_LARGE}
            position={TOOLTIP_POSITION.PORTAL}
            maxWidth={350}
            iconSize={24}
            iconClassName="mr-2"
          />
        </div>
      ) : (
        <div
          className={`items-list-row__border d-flex flex-row align-items-center cursor-pointer ${
            !isFranchiseEnabled ? 'w-11' : 'w-8'
          } justify-content-around`}
        >
          {/* status */}
          {user && user.status !== undefined ? renderSwitcher(user.status, changeStatus) : <Skeleton width={50} height={19.2} />}
          {/* delete/restore block */}
          {user ? (
            <Icon
              name={Icons.Trash}
              onPress={deleteUser}
              size={24}
              disabled={disabled}
              className="delete-restore-block__btn-delete"
              disabledClassName="delete-restore-block__btn-delete_disabled"
            />
          ) : (
            <Skeleton width={50} height={19.2} />
          )}
        </div>
      )}
    </li>
  );
});

export default UserListRow;
